import { isOnline } from '../connectivity/connectivityInfra';
import { showError, showInfo, showSuccess } from './appToast';

export type ScreenRefreshResult = {
  /** Muestra info en lugar de éxito (sync parcial, avisos operativos). */
  partialMessage?: string;
  /** No muestra toast (p. ej. refresh sin contexto válido). */
  silent?: boolean;
};

export const SCREEN_REFRESH_COPY = {
  successTitle: 'Actualizado',
  offlineTitle: 'Sin conexión',
  partialDefaultMessage: 'Algunos datos no se sincronizaron.',
  errorTitle: 'No se pudo actualizar',
  errorDefaultMessage: 'Reintentá en unos segundos.',
} as const;

export async function resolveScreenRefreshOnline(): Promise<boolean> {
  try {
    return await isOnline();
  } catch {
    return false;
  }
}

export function presentScreenRefreshFeedback(input: {
  online: boolean;
  result?: ScreenRefreshResult | void;
  error?: unknown;
}): void {
  const { online, result, error } = input;

  if (error) {
    const message =
      error instanceof Error ? error.message : SCREEN_REFRESH_COPY.errorDefaultMessage;
    showError(SCREEN_REFRESH_COPY.errorTitle, message);
    return;
  }

  if (result?.silent) {
    return;
  }

  if (!online) {
    showInfo(SCREEN_REFRESH_COPY.offlineTitle);
    return;
  }

  if (result?.partialMessage) {
    showInfo(result.partialMessage || SCREEN_REFRESH_COPY.partialDefaultMessage);
    return;
  }

  showSuccess(SCREEN_REFRESH_COPY.successTitle);
}

export type RunScreenRefreshOptions = {
  feedback?: boolean;
};

/**
 * Ejecuta un reload con guard de reentrada, estado refreshing y toast unificado.
 * Usar vía `useScreenRefresh` en pantallas; exportado para tests y casos puntuales.
 */
export async function runScreenRefresh(
  reload: () => Promise<ScreenRefreshResult | void>,
  options: RunScreenRefreshOptions & {
    setRefreshing: (value: boolean) => void;
    inFlightRef: { current: boolean };
  }
): Promise<void> {
  const { feedback = true, setRefreshing, inFlightRef } = options;

  if (inFlightRef.current) {
    return;
  }

  inFlightRef.current = true;
  setRefreshing(true);

  let online = false;
  let result: ScreenRefreshResult | void;
  let error: unknown;

  try {
    online = await resolveScreenRefreshOnline();
    result = await reload();
  } catch (e) {
    error = e;
    result = undefined;
  } finally {
    inFlightRef.current = false;
    setRefreshing(false);
  }

  if (feedback) {
    presentScreenRefreshFeedback({ online, result, error });
  }
}
